import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Shield, Loader2, Eye, EyeOff, Mail, ArrowLeft, ArrowRight, HelpCircle, CheckCircle, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { db } from "@/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import WebGLShader from "@/components/we360/WebGLShader";
import { getSavedB2BInvite, fetchCompanyName } from "@/services/b2bInviteService";

// Password strength indicator
function PasswordStrength({ password }) {
  if (!password) return null;
  
  const hasLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  
  const score = [hasLength, hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length;
  
  const getStrength = () => {
    if (score <= 2) return { label: "Débil", color: "#C0453A", width: "33%" };
    if (score <= 3) return { label: "Media", color: "#F9A01B", width: "66%" };
    return { label: "Fuerte", color: "#157346", width: "100%" };
  };
  
  const strength = getStrength();
  
  return (
    <div className="mt-2">
      <div className="flex justify-between items-center mb-1">
        <span className="text-[10px] font-semibold text-[#71644C]">Fortaleza:</span>
        <span className="text-[10px] font-bold" style={{ color: strength.color }}>{strength.label}</span>
      </div>
      <div className="h-1 rounded-full bg-[#E2D9CB]">
        <div 
          className="h-full rounded-full transition-all duration-300" 
          style={{ background: strength.color, width: strength.width }}
        />
      </div>
    </div>
  );
}

// Email input step
function EmailStep({ email, setEmail, onContinue, onBack, loading, error }) {
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const [touched, setTouched] = useState(false);
  const showInlineError = touched && (!email || !isValidEmail);
  const errorMsg = !email ? "Ingresa tu correo" : "Ingresa un correo válido";

  const handleContinue = () => {
    setTouched(true);
    if (!isValidEmail) return;
    onContinue();
  };
  
  return (
    <div className="animate-fadeIn">
      <p className="text-sm mb-5 font-semibold text-[#2D2A24]">
        Ingresa tu correo electrónico
      </p>
      
      {error && (
        <div 
          className="rounded-xl p-3 mb-5 text-sm bg-[#FDECEA] text-[#C62828] border border-[#FFCDD2]"
        >
          {error}
        </div>
      )}
      
      <div className="relative mb-6">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => { if (email) setTouched(true); }}
          placeholder=" "
          className={`block py-3 px-4 w-full text-[15px] font-semibold text-[#2D2A24] bg-white/40 rounded-xl border-1.5 border-[#E2D9CB] appearance-none focus:outline-none focus:ring-2 focus:ring-[#00628F]/10 focus:border-[#00628F] peer transition-all ${
            showInlineError ? "border-[#C0453A] focus:border-[#C0453A] focus:ring-[#C0453A]/10" : ""
          }`}
          autoFocus
          data-testid="email-input"
        />
        <label
          className="peer-focus:font-semibold absolute text-sm text-[#71644C] duration-200 transform -translate-y-4 scale-75 top-1.5 z-10 origin-[0] bg-white/95 px-2 rounded-md left-3 peer-focus:left-3 peer-focus:text-[#00628F] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 pointer-events-none"
        >
          Correo electrónico
        </label>
        {showInlineError && (
          <p className="absolute -bottom-5 left-1 text-[11px] font-medium text-[#C0453A] pointer-events-none">
            {errorMsg}
          </p>
        )}
      </div>
      
      <button
        onClick={handleContinue}
        disabled={loading}
        className={`w-full cursor-pointer active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mb-4 py-3.5 rounded-xl font-semibold text-sm text-white border-none ${
          isValidEmail ? "bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/20 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]" : "bg-[#B5A998]"
        }`}
        data-testid="email-continue-btn"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : null}
        <span>{loading ? "Verificando..." : "Continuar"}</span>
        {!loading && <ArrowRight size={16} />}
      </button>
      
      <button
        onClick={onBack}
        className="w-full flex items-center justify-center gap-2 cursor-pointer bg-transparent border-none text-[#00628F] text-[13px] font-semibold hover:underline"
        data-testid="email-back-btn"
      >
        <ArrowLeft size={14} />
        <span>Volver a opciones de inicio de sesión</span>
      </button>
    </div>
  );
}

// Login step — shown when system detects existing account
function LoginStep({ email, onBack, onForgotPassword, onSwitchToRegister, loading, error, onLogin }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);
  const showInlineError = touched && !password;

  return (
    <div className="animate-fadeIn">
      <p className="text-base mb-1 font-semibold text-[#2D2A24]">Bienvenido/a de vuelta</p>
      <p className="text-[13px] mb-5 text-[#71644C] font-medium">{email}</p>

      {error && (
        <div className="rounded-xl p-3 mb-5 text-sm bg-[#FDECEA] text-[#C62828] border border-[#FFCDD2]" data-testid="auth-error">
          {error}
        </div>
      )}

      <div className="relative mb-6">
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder=" "
            className={`block py-3 px-4 pr-10 w-full text-[15px] font-semibold text-[#2D2A24] bg-white/40 rounded-xl border-1.5 border-[#E2D9CB] appearance-none focus:outline-none focus:ring-2 focus:ring-[#00628F]/10 focus:border-[#00628F] peer transition-all ${
              showInlineError ? "border-[#C0453A] focus:border-[#C0453A] focus:ring-[#C0453A]/10" : ""
            }`}
            autoFocus
            data-testid="auth-password-input"
          />
          <label
            className="peer-focus:font-semibold absolute text-sm text-[#71644C] duration-200 transform -translate-y-4 scale-75 top-1.5 z-10 origin-[0] bg-white/95 px-2 rounded-md left-3 peer-focus:left-3 peer-focus:text-[#00628F] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 pointer-events-none"
          >
            Contraseña
          </label>
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer bg-transparent border-none text-[#71644C] hover:text-[#2D2A24]" data-testid="auth-toggle-password">
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {showInlineError && (
          <p className="absolute -bottom-5 left-1 text-[11px] font-medium text-[#C0453A] pointer-events-none">
            Ingresa tu contraseña
          </p>
        )}
      </div>

      <button onClick={() => { setTouched(true); if (password) onLogin(password); }} disabled={loading} className={`w-full cursor-pointer active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mb-4 py-3.5 rounded-xl font-semibold text-sm text-white border-none ${
        password ? "bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/20 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]" : "bg-[#B5A998]"
      }`} data-testid="auth-submit-btn">
        {loading ? <Loader2 size={18} className="animate-spin" /> : null}
        <span>{loading ? "Iniciando sesión..." : "Iniciar sesión"}</span>
      </button>

      <div className="flex justify-between items-center mb-5">
        <button onClick={onForgotPassword} className="cursor-pointer text-[12px] bg-transparent border-none text-[#00628F] font-semibold hover:underline" data-testid="auth-forgot-password-btn">
          ¿Olvidaste tu contraseña?
        </button>
        <button onClick={onBack} className="cursor-pointer text-[12px] flex items-center gap-1 bg-transparent border-none text-[#71644C] font-semibold hover:text-[#2D2A24]" data-testid="auth-change-email-btn">
          <ArrowLeft size={12} /><span>Cambiar email</span>
        </button>
      </div>

      <div className="text-center pt-4 border-t border-[#E2D9CB]">
        <p className="text-xs text-[#71644C]">
          ¿No tienes cuenta?{" "}
          <button onClick={onSwitchToRegister} className="cursor-pointer bg-transparent border-none text-[#00628F] font-bold text-[12px] hover:underline" data-testid="auth-switch-to-register">
            Crear una aquí
          </button>
        </p>
      </div>
    </div>
  );
}

// Register step — shown when system doesn't find the email
function RegisterStep({ email, onBack, onSwitchToLogin, loading, error, onRegister }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});

  const blur = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const pwError = touched.password ? (password ? (password.length < 8 ? "Mínimo 8 caracteres" : null) : "Ingresa una contraseña") : null;
  const confirmError = touched.confirm ? (confirmPassword ? (password !== confirmPassword ? "Las contraseñas no coinciden" : null) : "Confirma tu contraseña") : null;
  const canSubmit = password.length >= 8 && password === confirmPassword;

  const handleRegister = () => {
    setTouched({ password: true, confirm: true });
    if (!canSubmit) return;
    onRegister(password);
  };

  return (
    <div className="animate-fadeIn">
      <p className="text-base mb-1 font-semibold text-[#2D2A24]">Crea tu cuenta</p>
      <p className="text-[13px] mb-5 text-[#71644C] font-medium">{email}</p>

      {error && (
        <div className="rounded-xl p-3 mb-5 text-sm bg-[#FDECEA] text-[#C62828] border border-[#FFCDD2]" data-testid="auth-error">
          {error}
        </div>
      )}

      <div className="relative mb-6">
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => blur("password")}
            placeholder=" "
            className={`block py-3 px-4 pr-10 w-full text-[15px] font-semibold text-[#2D2A24] bg-white/40 rounded-xl border-1.5 border-[#E2D9CB] appearance-none focus:outline-none focus:ring-2 focus:ring-[#00628F]/10 focus:border-[#00628F] peer transition-all ${
              pwError ? "border-[#C0453A] focus:border-[#C0453A] focus:ring-[#C0453A]/10" : ""
            }`}
            autoFocus
            data-testid="auth-password-input"
          />
          <label
            className="peer-focus:font-semibold absolute text-sm text-[#71644C] duration-200 transform -translate-y-4 scale-75 top-1.5 z-10 origin-[0] bg-white/95 px-2 rounded-md left-3 peer-focus:left-3 peer-focus:text-[#00628F] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 pointer-events-none"
          >
            Contraseña (mínimo 8 caracteres)
          </label>
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer bg-transparent border-none text-[#71644C] hover:text-[#2D2A24]" data-testid="auth-toggle-password">
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {pwError && (
          <p className="absolute -bottom-5 left-1 text-[11px] font-medium text-[#C0453A] pointer-events-none">
            {pwError}
          </p>
        )}
        <PasswordStrength password={password} />
      </div>

      <div className="relative mb-6">
        <input
          type={showPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onBlur={() => blur("confirm")}
          placeholder=" "
          className={`block py-3 px-4 w-full text-[15px] font-semibold text-[#2D2A24] bg-white/40 rounded-xl border-1.5 border-[#E2D9CB] appearance-none focus:outline-none focus:ring-2 focus:ring-[#00628F]/10 focus:border-[#00628F] peer transition-all ${
            confirmError ? "border-[#C0453A] focus:border-[#C0453A] focus:ring-[#C0453A]/10" : ""
          }`}
          data-testid="auth-confirm-password-input"
        />
        <label
          className="peer-focus:font-semibold absolute text-sm text-[#71644C] duration-200 transform -translate-y-4 scale-75 top-1.5 z-10 origin-[0] bg-white/95 px-2 rounded-md left-3 peer-focus:left-3 peer-focus:text-[#00628F] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-4 pointer-events-none"
        >
          Confirmar contraseña
        </label>
        {confirmError && (
          <p className="absolute -bottom-5 left-1 text-[11px] font-medium text-[#C0453A] pointer-events-none">
            {confirmError}
          </p>
        )}
      </div>

      <button onClick={handleRegister} disabled={loading} className={`w-full cursor-pointer active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mb-4 py-3.5 rounded-xl font-semibold text-sm text-white border-none ${
        canSubmit ? "bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/20 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]" : "bg-[#B5A998]"
      }`} data-testid="auth-submit-btn">
        {loading ? <Loader2 size={18} className="animate-spin" /> : null}
        <span>{loading ? "Creando cuenta..." : "Crear cuenta"}</span>
      </button>

      <div className="flex justify-end mb-5">
        <button onClick={onSwitchToLogin} className="cursor-pointer text-[12px] flex items-center gap-1 bg-transparent border-none text-[#71644C] font-semibold hover:text-[#2D2A24]" data-testid="auth-switch-to-login">
          <ArrowLeft size={12} /><span>Volver a iniciar sesión</span>
        </button>
      </div>
    </div>
  );
}

// Social account exists — user needs to use Google
function SocialExistsStep({ email, onGoogleLogin, onBack, loading }) {
  return (
    <div className="animate-fadeIn text-center">
      <div 
        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-[#FFF3E0] shadow-md shadow-amber-500/5"
      >
        <svg width="32" height="32" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      </div>
      <p className="text-base font-semibold mb-2 text-[#2D2A24]">
        Este correo ya usa Google
      </p>
      <p className="text-xs mb-5 text-[#71644C] px-2 leading-relaxed">
        El correo <strong>{email}</strong> ya está registrado con Google. Usa el botón de abajo para iniciar sesión.
      </p>
      <button
        onClick={onGoogleLogin}
        disabled={loading}
        className="w-full cursor-pointer active:scale-[0.97] transition-all flex items-center justify-center gap-3 py-3.5 rounded-xl text-sm font-semibold text-white border-none bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/20 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mb-4"
        data-testid="social-google-login-btn"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : (
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        )}
        <span>{loading ? "Conectando..." : "Iniciar sesión con Google"}</span>
      </button>
      <button
        onClick={onBack}
        className="w-full flex items-center justify-center gap-2 cursor-pointer bg-transparent border-none text-[#71644C] text-[13px] font-semibold hover:text-[#2D2A24]"
        data-testid="social-back-btn"
      >
        <ArrowLeft size={14} />
        <span>Volver a opciones de inicio de sesión</span>
      </button>
    </div>
  );
}

// Password reset success
function ResetSuccessStep({ email, onBack }) {
  return (
    <div className="animate-fadeIn text-center">
      <div 
        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-[#E8F4F8] shadow-md shadow-sky-600/5"
      >
        <Mail size={32} className="text-[#00628F]" />
      </div>
      <p className="text-base font-semibold mb-2 text-[#2D2A24]">
        ¡Correo enviado!
      </p>
      <p className="text-xs mb-5 text-[#71644C] px-2 leading-relaxed">
        Te enviamos un enlace para restablecer tu contraseña a <strong>{email}</strong>. Revisa tu bandeja de entrada.
      </p>
      <button
        onClick={onBack}
        className="cursor-pointer active:scale-[0.97] transition-all py-3 px-6 rounded-xl bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/20 hover:shadow-xl hover:scale-[1.02] text-sm font-semibold text-white border-none"
        data-testid="reset-back-btn"
      >
        Volver a iniciar sesión
      </button>
    </div>
  );
}

export default function Login({ defaultMode = null }) {
  const navigate = useNavigate();
  const { 
    loginWithGoogle, loginAsGuest, loginAsDemo, 
    checkEmailExists, loginWithEmail, registerWithEmail, resetPassword,
    user 
  } = useAuth();
  const { profile, loading: profileLoading, onboardingCompleted } = useUserProfile();
  
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [error, setError] = useState(null);
  const [b2bCompany, setB2bCompany] = useState(null);
  
  // Email flow state
  const [showEmailFlow, setShowEmailFlow] = useState(defaultMode === "register" || defaultMode === "login");
  const [emailStep, setEmailStep] = useState(defaultMode === "register" ? "register" : "email"); // email, login, register, reset-success, social-exists
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(null);

  useEffect(() => {
    const invite = getSavedB2BInvite();
    if (invite?.email && !email) {
      setEmail(invite.email);
    }
    if (invite?.cid) {
      fetchCompanyName(invite.cid).then((name) => {
        if (name) setB2bCompany(name);
      });
    }
    if (defaultMode === "register" || defaultMode === "login") {
      setShowEmailFlow(true);
      if (defaultMode === "register") {
        setEmailStep("register");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMode]);

  // Rescue modal state ("No puedo ingresar")
  const [showRescue, setShowRescue] = useState(false);
  const [rescueEmail, setRescueEmail] = useState("");
  const [rescueDob, setRescueDob] = useState("");
  const [rescueMsg, setRescueMsg] = useState("");
  const [rescueSending, setRescueSending] = useState(false);
  const [rescueSent, setRescueSent] = useState(false);
  const [rescueError, setRescueError] = useState(null);

  // If already logged in and profile loaded, redirect appropriately
  if (user && !profileLoading) {
    if (user.isDemo) {
      navigate("/dashboard");
      return null;
    }
    const hasProfileData = profile?.completedAt || (profile?.nombres && profile?.fechaNacimiento && profile?.peso) || onboardingCompleted;
    if (hasProfileData) {
      navigate("/dashboard");
    } else {
      navigate("/onboarding");
    }
    return null;
  }

  // Show loading while profile is loading after login
  if (user && profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FCFAEE]">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin mx-auto mb-4 text-[#00628F]" />
          <p className="text-sm text-[#71644C] font-semibold">Cargando tu perfil...</p>
        </div>
      </div>
    );
  }

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    const result = await loginWithGoogle();
    if (!result.success && result.error) {
      setError(result.error);
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    setError(null);
    const result = await loginAsGuest();
    if (!result.success) {
      setError(result.error);
      setGuestLoading(false);
    }
  };

  const handleDemoLogin = () => {
    loginAsDemo();
    navigate("/dashboard");
  };

  // Email flow handlers
  const handleEmailContinue = async () => {
    setLoading(true);
    setEmailError(null);
    const result = await checkEmailExists(email);
    if (result.error) {
      setEmailError(result.error);
      setLoading(false);
      return;
    }
    if (result.exists && result.methods?.includes('google.com')) {
      setEmailStep("social-exists");
    } else if (result.exists) {
      setEmailStep("login");
    } else {
      setEmailStep("register");
    }
    setLoading(false);
  };

  const handleLogin = async (password) => {
    setLoading(true);
    setEmailError(null);
    const result = await loginWithEmail(email, password);
    if (!result.success) {
      setEmailError(result.error);
      setLoading(false);
    }
  };

  const handleRegister = async (password) => {
    setLoading(true);
    setEmailError(null);
    const result = await registerWithEmail(email, password);
    if (!result.success) {
      if (result.errorCode === 'social-account') {
        setEmailStep("social-exists");
        setLoading(false);
        return;
      }
      if (result.errorCode === 'email-already-in-use') {
        setEmailStep("login");
        setEmailError("Este correo ya está registrado. Ingresa tu contraseña para iniciar sesión.");
        setLoading(false);
        return;
      }
      setEmailError(result.error);
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setEmailError(null);
    const result = await resetPassword(email);
    if (result.success) {
      setEmailStep("reset-success");
    } else {
      setEmailError(result.error);
    }
    setLoading(false);
  };

  // Handle Google login from social-exists screen  
  const handleSocialGoogleLogin = async () => {
    setLoading(true);
    setEmailError(null);
    const result = await loginWithGoogle();
    if (!result.success && result.error) {
      setEmailError(result.error);
      setLoading(false);
    }
  };

  const resetEmailFlow = () => {
    setShowEmailFlow(false);
    setEmailStep("email");
    setEmail("");
    setEmailError(null);
  };

  // Render email flow screens
  const renderEmailFlow = () => {
    switch (emailStep) {
      case "email":
        return (
          <EmailStep
            email={email}
            setEmail={setEmail}
            onContinue={handleEmailContinue}
            onBack={resetEmailFlow}
            loading={loading}
            error={emailError}
          />
        );
      case "login":
        return (
          <LoginStep
            email={email}
            onBack={() => setEmailStep("email")}
            onForgotPassword={handleForgotPassword}
            onSwitchToRegister={() => { setEmailError(null); setEmailStep("register"); }}
            loading={loading}
            error={emailError}
            onLogin={handleLogin}
          />
        );
      case "register":
        return (
          <RegisterStep
            email={email}
            onBack={() => setEmailStep("email")}
            onSwitchToLogin={() => { setEmailError(null); setEmailStep("login"); }}
            loading={loading}
            error={emailError}
            onRegister={handleRegister}
          />
        );
      case "reset-success":
        return (
          <ResetSuccessStep
            email={email}
            onBack={() => setEmailStep("login")}
          />
        );
      case "social-exists":
        return (
          <SocialExistsStep
            email={email}
            onGoogleLogin={handleSocialGoogleLogin}
            onBack={resetEmailFlow}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen font-sans flex flex-col md:flex-row bg-[#FCFAEE] relative overflow-hidden"
      data-testid="login-page"
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out;
        }
      `}</style>
      
      {/* ── Left Pane (Visual & WebGL) ── */}
      <div className="hidden md:flex md:w-1/2 relative min-h-screen flex-col justify-between p-12 overflow-hidden bg-gradient-to-br from-[#FCFAEE] to-[#E2D9CB]">
        {/* Glow Effects */}
        <div className="glow-organic -top-20 -left-20 opacity-80" />
        <div className="glow-organic -bottom-20 -right-20 opacity-80" />
        
        {/* WebGL Wave Background */}
        <div className="absolute inset-0 z-0">
          <WebGLShader className="absolute inset-0 object-cover opacity-35" />
        </div>
        
        {/* Logo and Slogan */}
        <div className="relative z-10 flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/20"
          >
            <Shield size={24} color="white" />
          </div>
          <span className="text-xl font-extrabold text-[#2D2A24] tracking-wide" data-testid="login-title">WE 360</span>
        </div>
        
        {/* Center Content: Slogan in Glass Card */}
        <div className="relative z-10 my-auto max-w-lg">
          <div className="glass-panel p-8 rounded-2xl shadow-xl border border-white/40 mb-8 transform hover:scale-[1.01] transition-transform duration-300">
            <h2 className="text-3xl font-extrabold text-[#2D2A24] leading-snug mb-4">
              Salud preventiva para tu comunidad
            </h2>
            <p className="text-base text-[#71644C] leading-relaxed font-medium">
              Tecnología y bienestar en perfecta armonía. Monitorea biomarcadores clínicos con precisión científica desde cualquier dispositivo.
            </p>
          </div>
          
          {/* Bento Grid Mockups (BPM, HRV) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="glass-panel p-5 rounded-2xl shadow-md border border-white/50 hover:scale-[1.03] transition-transform duration-300 cursor-pointer group">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-[#71644C] uppercase tracking-wider">Ritmo Cardíaco</span>
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-extrabold text-[#2D2A24]">72</span>
                <span className="text-xs font-semibold text-[#71644C]">bpm</span>
              </div>
              <div className="mt-3 h-1.5 bg-[#E2D9CB] rounded-full overflow-hidden">
                <div className="h-full bg-rose-500 rounded-full w-[70%] group-hover:w-[85%] transition-all duration-500" />
              </div>
            </div>
            
            <div className="glass-panel p-5 rounded-2xl shadow-md border border-white/50 hover:scale-[1.03] transition-transform duration-300 cursor-pointer group">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-[#71644C] uppercase tracking-wider">Variabilidad (HRV)</span>
                <span className="w-2.5 h-2.5 rounded-full bg-[#2A9D8F]" />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-extrabold text-[#2D2A24]">48</span>
                <span className="text-xs font-semibold text-[#71644C]">ms</span>
              </div>
              <div className="mt-3 h-1.5 bg-[#E2D9CB] rounded-full overflow-hidden">
                <div className="h-full bg-[#2A9D8F] rounded-full w-[60%] group-hover:w-[75%] transition-all duration-500" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer info */}
        <div className="relative z-10 text-[11px] text-[#71644C] font-semibold flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span>Sistema de telemetría médica certificado v1.2.0</span>
        </div>
      </div>

      {/* ── Right Pane (Login Form) ── */}
      <div className="w-full md:w-1/2 min-h-screen flex flex-col justify-between py-12 px-6 md:px-12 relative overflow-hidden bg-[#FCFAEE]">
        {/* Background Decorative Glow */}
        <div className="glow-organic top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-60 md:opacity-40" />

        {/* Small top header for Mobile only */}
        <div className="flex flex-col items-center md:hidden mb-6 z-10">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 bg-gradient-to-br from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/20"
          >
            <Shield size={28} color="white" />
          </div>
          <h1 className="text-xl font-extrabold text-[#2D2A24] tracking-wide" data-testid="login-title">WE 360</h1>
          <p className="text-xs text-[#71644C] mt-1 font-medium">Salud preventiva para tu comunidad</p>
        </div>

        {/* Empty placeholder on Desktop to center content vertically */}
        <div className="hidden md:block" />

        {/* Main interactive login card */}
        <div className="w-full max-w-[400px] mx-auto z-10 my-auto">
          {/* Main Card Container */}
          <div className="glass-card rounded-2xl p-6 md:p-8 shadow-2xl relative border border-white/60" data-testid="login-card">
            {b2bCompany && (
              <div className="mb-4 p-3 rounded-xl bg-teal-50/90 border border-teal-200/80 flex items-center gap-2.5 text-left animate-fadeIn">
                <div className="w-8 h-8 rounded-lg bg-teal-600/10 flex items-center justify-center text-teal-700 shrink-0">
                  <Building2 size={16} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-teal-800 uppercase tracking-wider">Invitación Corporativa</p>
                  <p className="text-xs text-teal-950 font-semibold truncate">Ingresando con beneficios de {b2bCompany}</p>
                </div>
              </div>
            )}
            {showEmailFlow ? (
              renderEmailFlow()
            ) : (
              <>
                <p className="text-sm mb-5 font-bold text-[#2D2A24] text-center">
                  Inicia sesión para continuar
                </p>
                
                {error && (
                  <div 
                    className="rounded-xl p-3 mb-5 text-sm bg-[#FDECEA] text-[#C62828] border border-[#FFCDD2] text-center"
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}

                {/* Google Sign-In Button */}
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading || guestLoading}
                  data-testid="google-login-btn"
                  className="w-full cursor-pointer active:scale-[0.97] hover:scale-[1.02] transition-all flex items-center justify-center gap-3 py-3.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed mb-3.5"
                >
                  {loading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  <span>{loading ? "Conectando..." : "Continuar con Google"}</span>
                </button>

                {/* Email Sign-In Button */}
                <button
                  onClick={() => setShowEmailFlow(true)}
                  disabled={loading || guestLoading}
                  data-testid="email-login-btn"
                  className="w-full cursor-pointer active:scale-[0.97] hover:scale-[1.02] transition-all flex items-center justify-center gap-3 py-3.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed mb-3.5"
                >
                  <Mail size={20} className="text-[#71644C]" />
                  <span>Continuar con email</span>
                </button>
              </>
            )}
          </div>

          {/* Demo Account Section */}
          {!showEmailFlow && (
            <div className="glass-panel p-5 mt-5 rounded-2xl border border-white/40 shadow-md text-center" data-testid="demo-section">
              <button
                onClick={handleDemoLogin}
                disabled={loading || guestLoading}
                data-testid="demo-login-btn"
                className="w-full cursor-pointer active:scale-[0.97] hover:scale-[1.02] transition-all flex items-center justify-center gap-3 py-3.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#71644C] shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye size={20} />
                <span>Ver cuenta demo</span>
              </button>
              <p className="text-[11px] font-semibold mt-3 text-[#B5A998]">
                Explora la app con datos de ejemplo de José Pérez
              </p>
            </div>
          )}
        </div>

        {/* Footer elements */}
        <div className="w-full max-w-[400px] mx-auto text-center mt-8 z-10">
          <p className="text-[11px] font-semibold text-[#B5A998]">
            Al continuar, aceptas nuestro{" "}
            <a href="https://saluddigital360.com/acuerdo-usuario" target="_blank" rel="noopener noreferrer" className="text-[#00628F] underline hover:text-[#2A9D8F] transition-colors">
              acuerdo de usuario
            </a>{" "}
            y{" "}
            <a href="https://saluddigital360.com/politicas-privacidad" target="_blank" rel="noopener noreferrer" className="text-[#00628F] underline hover:text-[#2A9D8F] transition-colors">
              política de privacidad
            </a>
          </p>
          <p className="text-xs mt-3.5">
            <Link to="/changelog" className="text-[11px] font-bold text-[#B5A998] hover:text-[#71644C] transition-colors" data-testid="changelog-link">
              Novedades v1.2.0
            </Link>
          </p>

          {/* Rescue link */}
          {!showEmailFlow && (
            <button
              onClick={() => { setShowRescue(true); setRescueSent(false); setRescueError(null); setRescueEmail(""); setRescueDob(""); setRescueMsg(""); }}
              className="text-[11px] font-bold mt-2.5 text-[#94A3B8] hover:text-[#71644C] transition-colors bg-none border-none cursor-pointer underline"
              data-testid="rescue-access-btn"
            >
              ¿No puedes ingresar? Solicita recuperación de acceso
            </button>
          )}
        </div>
      </div>

      {/* ── Rescue Modal ── */}
      {showRescue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/40 animate-fadeIn">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-white/60 relative" data-testid="rescue-modal">
            {rescueSent ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 bg-[#DCFCE7] shadow-md shadow-emerald-500/5">
                  <CheckCircle size={24} className="text-[#16A34A]" />
                </div>
                <h3 className="font-bold mb-2 text-[#1E293B]">Solicitud enviada</h3>
                <p className="text-sm mb-4 text-[#475569] leading-relaxed">
                  Recibimos tu solicitud. El equipo de soporte de WE360 revisará tu caso y se pondrá en contacto contigo en las próximas 24-48 horas.
                </p>
                <button
                  onClick={() => setShowRescue(false)}
                  className="w-full py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#00628F] to-[#0080A0] text-white border-none cursor-pointer shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  Cerrar
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[#EFF6FF] shadow-sm">
                    <HelpCircle size={20} className="text-[#0284C7]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E293B]">Recuperar acceso</h3>
                    <p className="text-[11px] font-semibold text-[#64748B]">Soporte WE360 revisará tu caso</p>
                  </div>
                </div>
                <p className="text-xs mb-4 text-[#64748B] leading-relaxed">
                  Si ya no tienes acceso a tu cuenta corporativa, completa este formulario. Te contactaremos para verificar tu identidad y recuperar tus datos.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wide block mb-1.5 text-[#94A3B8]">
                      Email corporativo anterior *
                    </label>
                    <input
                      type="email"
                      value={rescueEmail}
                      onChange={(e) => setRescueEmail(e.target.value)}
                      placeholder="tu.nombre@empresa.com"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2E8F0] text-sm text-[#1E293B] font-semibold bg-white/50 focus:outline-none focus:ring-2 focus:ring-[#00628F]/10 focus:border-[#00628F] transition-all"
                      data-testid="rescue-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wide block mb-1.5 text-[#94A3B8]">
                      Fecha de nacimiento *
                    </label>
                    <input
                      type="date"
                      value={rescueDob}
                      onChange={(e) => setRescueDob(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2E8F0] text-sm text-[#1E293B] font-semibold bg-white/50 focus:outline-none focus:ring-2 focus:ring-[#00628F]/10 focus:border-[#00628F] transition-all"
                      data-testid="rescue-dob-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wide block mb-1.5 text-[#94A3B8]">
                      Mensaje adicional (opcional)
                    </label>
                    <textarea
                      value={rescueMsg}
                      onChange={(e) => setRescueMsg(e.target.value)}
                      placeholder="Describe brevemente tu situación..."
                      rows={2}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2E8F0] text-sm text-[#1E293B] font-semibold bg-white/50 focus:outline-none focus:ring-2 focus:ring-[#00628F]/10 focus:border-[#00628F] transition-all resize-none"
                      data-testid="rescue-msg-input"
                    />
                  </div>
                </div>

                {rescueError && (
                  <p className="text-xs mt-2.5 text-[#DC2626] font-semibold">{rescueError}</p>
                )}

                <div className="flex gap-2.5 mt-5">
                  <button
                    onClick={() => setShowRescue(false)}
                    className="flex-1 py-2.5 rounded-xl text-sm border border-[#E2E8F0] text-[#64748B] font-semibold hover:bg-[#F8FAFC] transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    disabled={!rescueEmail || !rescueDob || rescueSending}
                    onClick={async () => {
                      if (!rescueEmail || !rescueDob) return;
                      setRescueSending(true);
                      setRescueError(null);
                      try {
                        await addDoc(collection(db, "support_requests"), {
                          type: "rescue_access",
                          corporateEmail: rescueEmail.trim().toLowerCase(),
                          dateOfBirth: rescueDob,
                          message: rescueMsg.trim() || null,
                          submittedAt: serverTimestamp(),
                          status: "pending",
                        });
                        setRescueSent(true);
                      } catch {
                        setRescueError("Error al enviar. Intenta de nuevo o escribe a soporte@saluddigital360.com");
                      } finally {
                        setRescueSending(false);
                      }
                    }}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 border-none transition-all"
                    style={{
                      background: rescueEmail && rescueDob ? "linear-gradient(135deg,#00628F 0%,#2A9D8F 100%)" : "#F1F5F9",
                      color: rescueEmail && rescueDob ? "white" : "#94A3B8",
                      cursor: rescueEmail && rescueDob && !rescueSending ? "pointer" : "not-allowed",
                      boxShadow: rescueEmail && rescueDob ? "0 4px 12px rgba(0,98,143,0.15)" : "none"
                    }}
                    data-testid="rescue-submit-btn"
                  >
                    {rescueSending ? <Loader2 size={14} className="animate-spin" /> : null}
                    Enviar solicitud
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
